import { useState } from 'react';
import { Search, Grid, List, Box } from 'lucide-react';
import { useModels, useLinkDomains } from '@/features/models/hooks';
import { ModelCard } from '@/features/models/components/ModelCard';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';

// Helper to create display names from domains
function formatDomainLabel(domain: string): string {
  const domainNames: Record<string, string> = {
    'makerworld.com': 'MakerWorld',
    'printables.com': 'Printables',
    'thingiverse.com': 'Thingiverse',
    'thangs.com': 'Thangs',
    'cults3d.com': 'Cults3D',
    'myminifactory.com': 'MyMiniFactory',
  };

  return domainNames[domain] || domain;
}

interface CollectionModelBrowserProps {
  collectionId: string;
}

export function CollectionModelBrowser({ collectionId }: CollectionModelBrowserProps) {
  // Local state for filters
  const [search, setSearch] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'addedAt'>('addedAt');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);

  // Fetch unique link domains for filter dropdown
  const { data: linkDomains = [] } = useLinkDomains();

  // Fetch models for this collection only (not children)
  const { data, isLoading, error } = useModels({
    page,
    limit: 25,
    search: search || undefined,
    sourceType: sourceType || undefined,
    sortBy,
    collectionIds: [collectionId], // Only this collection, not children
  });

  const models = data?.items || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {/* Search */}
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search models..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1); // Reset to page 1 when search changes
            }}
            leftIcon={<Search className="h-5 w-5" />}
          />
        </div>

        {/* Source Type Filter */}
        <div className="w-full sm:w-48">
          <Select
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All Sources' },
              { value: 'file', label: 'Files' },
              ...linkDomains.map((domain) => ({
                value: domain,
                label: formatDomainLabel(domain),
              })),
            ]}
          />
        </div>

        {/* Sort */}
        <div className="w-full sm:w-48">
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'createdAt' | 'addedAt')}
            options={[
              { value: 'addedAt', label: 'Recently Added' },
              { value: 'name', label: 'Name (A-Z)' },
              { value: 'createdAt', label: 'Recently Created' },
            ]}
          />
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-1 p-1 bg-background-subtle rounded-lg flex-shrink-0">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'grid'
                ? 'bg-background text-foreground'
                : 'text-foreground-muted hover:text-foreground'
            }`}
            aria-label="Grid view"
          >
            <Grid className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list'
                ? 'bg-background text-foreground'
                : 'text-foreground-muted hover:text-foreground'
            }`}
            aria-label="List view"
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Models Grid/List */}
      {isLoading ? (
        <div className="rounded border border-border bg-background-subtle p-12 text-center">
          <p className="text-foreground-muted">Loading models...</p>
        </div>
      ) : error ? (
        <div className="rounded border border-border bg-background-subtle p-12 text-center">
          <p className="text-error">Failed to load models</p>
        </div>
      ) : models.length === 0 ? (
        <div className="rounded border border-border bg-background-subtle p-4">
          <EmptyState
            icon={<Box className="h-10 w-10" />}
            title="No models found"
            description={search || sourceType ? 'Try adjusting your filters' : 'Add models to this collection from the Models page'}
          />
        </div>
      ) : (
        <>
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                : 'space-y-4'
            }
          >
            {models.map((model) => (
              <ModelCard key={model.id} model={model} />
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <p className="text-sm text-foreground-muted">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total models)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 text-sm font-medium rounded-md border border-border bg-background hover:bg-background-subtle disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                  disabled={page === pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium rounded-md border border-border bg-background hover:bg-background-subtle disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
